import { InputFormBlock } from "components/app/input-form-block";
import {
    ClientAndBestContact,
    useClientNameOrder,
} from "components/clients/client-name-order";
import { LocaleContext } from "components/locale-provider/locale-provider";
import { ReadOnlyField } from "components/ui/read-only-field";
import * as React from "react";
import {
    approximateAge,
    formattedPhone,
    hasSharedContactDetails,
    humanDate,
    LabeledSharedContactDetails,
    present,
    SharedContact,
} from "./shared-contact-details";

export interface SharedLeadContacts {
    selfContact: SharedContact;
    bestContact: SharedContact;
}

export const hasSharedLeadDetails = (lead: SharedLeadContacts): boolean =>
    !!present(lead.selfContact.name) ||
    !!present(lead.bestContact.name) ||
    hasSharedContactDetails(lead.selfContact) ||
    hasSharedContactDetails(lead.bestContact);

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">{children}</div>
);

const ContactField: React.FC<{ label: string; value: string | null }> = ({
    label,
    value,
}) => (
    <ReadOnlyField label={label} testId={`contact-field-${label}`}>
        {value}
    </ReadOnlyField>
);

const ContactFieldRows: React.FC<{ contact: SharedContact }> = ({ contact }) => {
    const locale = React.useContext(LocaleContext);
    return (
        <>
            <FieldRow>
                <ContactField label="Name" value={present(contact.name)} />
                <ContactField
                    label="Preferred name"
                    value={present(contact.preferredName)}
                />
            </FieldRow>
            <FieldRow>
                <ContactField
                    label="Cell phone number"
                    value={formattedPhone(contact.cellPhone)}
                />
                <ContactField
                    label="Phone number (other)"
                    value={formattedPhone(contact.phone1)}
                />
            </FieldRow>
            <ContactField label="Email" value={present(contact.email1)} />
            <ContactField label="Address" value={present(contact.address1)} />
            <FieldRow>
                <ContactField label="City" value={present(contact.city)} />
                <ContactField
                    label={locale.strings.state}
                    value={present(contact.state)}
                />
                <ContactField label={locale.strings.zip} value={present(contact.zip)} />
            </FieldRow>
        </>
    );
};

const BirthdayField: React.FC<{ contact: SharedContact }> = ({ contact }) => {
    const birthday = present(contact.birthday);
    if (birthday && contact.isBirthdayFake) {
        return <ContactField label="Age" value={approximateAge(birthday)} />;
    }
    return <ContactField label="Date of birth" value={humanDate(birthday)} />;
};

const ContactFieldsTile: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <InputFormBlock>
        <div className="space-y-2 lg:space-y-4">
            <h3 className="text-lg text-gray-700">{title}</h3>
            <div className="space-y-2 md:space-y-4">{children}</div>
        </div>
    </InputFormBlock>
);

const ClientInfoFields: React.FC<{ contact: SharedContact }> = ({ contact }) => (
    <ContactFieldsTile title="Client Info">
        <ContactFieldRows contact={contact} />
        <BirthdayField contact={contact} />
    </ContactFieldsTile>
);

const BestContactFields: React.FC<{ contact: SharedContact }> = ({ contact }) => (
    <ContactFieldsTile title="Best Contact Person">
        <ContactFieldRows contact={contact} />
    </ContactFieldsTile>
);

export const SharedLeadDetailsBlock: React.FC<{
    lead: SharedLeadContacts;
    expanded: boolean;
}> = ({ lead, expanded }) => {
    const order = useClientNameOrder();

    if (expanded) {
        return (
            <div
                data-testid="shared-lead-details-expanded"
                className="grid max-w-7xl grid-cols-1 items-start gap-2 lg:grid-cols-2 lg:gap-4">
                <ClientAndBestContact
                    order={order}
                    client={<ClientInfoFields contact={lead.selfContact} />}
                    bestContact={<BestContactFields contact={lead.bestContact} />}
                />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <ClientAndBestContact
                order={order}
                client={
                    <LabeledSharedContactDetails
                        label="Client"
                        contact={lead.selfContact}
                    />
                }
                bestContact={
                    <LabeledSharedContactDetails
                        label="Best contact"
                        contact={lead.bestContact}
                    />
                }
            />
        </div>
    );
};
